import React, { type ReactNode } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';

import ClearIcon from './clear_filter_icon';
import { OverlayTrigger, Popover } from 'react-bootstrap-5';

interface FilterButtonProps {
  'aria-label'?: string;
  children: ReactNode;
  className?: string;
  constantWidth?: boolean;
  disabled?: boolean;
  filtersApplied?: boolean;
  filterTag?: string;
  label: string | ReactNode;
  name: string;
  selectedLength?: number;
  onClear?: () => void;
}

export const FilterButton: React.FC<FilterButtonProps> = ({
  'aria-label': ariaLabel,
  children,
  className,
  constantWidth,
  disabled,
  filtersApplied,
  filterTag,
  label,
  name,
  selectedLength,
  onClear,
}: FilterButtonProps) => {
  const renderFilterTag = (): ReactNode => {
    if (filterTag) {
      return <span>({filterTag})</span>;
    } else if (selectedLength) {
      return <span>({selectedLength})</span>;
    } else if (constantWidth || filtersApplied) {
      return (
        <div
          className={classNames('filter-dot', {
            invisible: !filtersApplied,
          })}
        >
          <FontAwesomeIcon icon={faCircle} size="xs" />
        </div>
      );
    }
    return null;
  };

  const renderPopover = () => {
    return (
      <Popover className={name} id={name}>
        <Popover.Body className="overflow-y-auto" style={{ maxHeight: '600px' }}>
          {children}
        </Popover.Body>
      </Popover>
    );
  };

  const renderButton = (): ReactNode => {
    if (onClear) {
      return <ClearIcon onClear={onClear} />;
    }

    return '';
  };

  return (
    <OverlayTrigger
      overlay={renderPopover()}
      placement="bottom"
      rootClose
      trigger={disabled ? [] : 'click'}
    >
      <button
        aria-label={
          ariaLabel || (typeof label === 'string' ? label : undefined)
        }
        className={classNames(
          'form-select w-auto',
          { disabled: disabled },
          className
        )}
      >
        <span className="d-inline-flex text-nowrap gap-1 align-items-center">
          {label}
          {renderFilterTag()}
        </span>
        {renderButton()}
      </button>
    </OverlayTrigger>
  );
};

export default FilterButton;
